import type React from 'react';
import useDateTime from 'hooks/useDateTime';
import type { Article } from 'interfaces/content/articles/Articles';
import Breadcrumbs from 'components/Breadcrumbs';
import ArticleSeriesTop from 'components/ArticleSeries/ArticleSeriesTop';
import ContributorsByline from 'components/ContributorsByline';
import type { EventData } from 'services/Gtm/entities';
import { LANDING_PAGE_TEST_IDS } from 'constants/testsIds/landingPage';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import * as S from './LandingPageHeaderStyles';

export interface LandingPageHeaderProps {
  article: Article;
  trackingData: EventData;
}

const LandingPageHeader: React.FC<LandingPageHeaderProps> = ({ article, trackingData }) => {
  const { authors, breadcrumbs, dateGmt, modifiedGmt, showModifiedTimestamp, title, reviewedBy, series, url } = article;

  const localDate = useDateTime(dateGmt);
  const updatedDate = useDateTime(modifiedGmt);
  const isUpdated = !(localDate === updatedDate);
  const breadcrumbsData = (
    <Breadcrumbs
      {...breadcrumbs}
      hasBrandColor
    />
  );

  return (
    <S.LandingPageHeader className='landing-page-header'>
      <S.BreadcrumbsContainer>{breadcrumbsData}</S.BreadcrumbsContainer>
      <S.Title
        as='h1'
        $fontFamily='saol'
        $size={{ default: 'level3', mdMin: 'large' }}
        data-cy={LANDING_PAGE_TEST_IDS.TITLE}
      >
        {title}
      </S.Title>
      <S.AuthorsContainer>
        <ContributorsByline
          authors={authors}
          trackingData={trackingData}
          reviewedBy={reviewedBy}
          dataCy={LANDING_PAGE_TEST_IDS.AUTHORS}
        />
      </S.AuthorsContainer>
      {showModifiedTimestamp && isUpdated ? (
        <S.LandingPageHeroDate data-cy={LANDING_PAGE_TEST_IDS.DATE}>Updated {updatedDate}</S.LandingPageHeroDate>
      ) : (
        <S.LandingPageHeroDate data-cy={LANDING_PAGE_TEST_IDS.DATE}>{localDate}</S.LandingPageHeroDate>
      )}
      {series.map((seriesList, idx) => {
        const displaySeries = seriesList.name && seriesList?.posts?.length > 0;
        if (!displaySeries) return null;
        return (
          <ArticleSeriesTop
            seriesID={`articleSeries${idx}`}
            key={seriesList.name}
            series={seriesList}
            articleURL={url}
            className='articleSeriesTop'
            dataCy={LANDING_PAGE_TEST_IDS.SERIES_TOP}
          />
        );
      })}
    </S.LandingPageHeader>
  );
};

export default withErrorBoundary(LandingPageHeader, { fragment: UIFragments.LANDING_PAGE_HEADER });
